<template>
  <section class="events">
    <HeaderComponent title="Eventos" titlemovil="Eventos" />
    <EventsComponentToolbar
      :games-by-round="gamesByRound"
      :categories="categories"
      @change-game-id="onChangeGameId"
      @change-category="onChangeCategory"
      @change-time="onChangeTime"
    />
    <div class="events__cont-table">
      <TableEvents :table-data="tableData" />
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import { regularLeague, cup, cupLegacyAbb } from '@/utils/play-off-abbreviations';

export default {
  name: 'Events',
  components: {
    HeaderComponent: () => import('@/components/ActiveTime/Elements/HeaderComponent'),
    EventsComponentToolbar: () => import('@/components/ActiveTime/Elements/EventsComponentToolbar'),
    TableEvents: () => import('@/components/ActiveTime/Elements/TableEvents'),
  },
  props: {
    /**
     * Seasons objects to fetch games
     * {Array<{id: number, type: number}>} seasons
     */
    seasons: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    tableData() {
      const tableData = [];
      if (this.gamesById && this.selectedGame && this.selectedCategory && this.selectedTime) {
        const game = this.gamesById.get(this.selectedGame.game_id);
        if (game) {
          const events = game.eventsById.get(this.selectedCategory.action_id) || [];
          events.forEach(event => {
            if (event.duration >= this.selectedTime) {
              const eventInfo = {
                homeTeamLogo: this.selectedGame.homeTeam.logo,
                awayTeamLogo: this.selectedGame.awayTeam.logo,
                teamName: event.team_name,
                playerName: event.player_name || '-',
                matchLapse: event.matchlapse,
                start: event.start,
                end: event.end,
                duration: event.duration,
                score: event.score_at_moment_of_play,
                comments: event.comments || '-',
              };
              tableData.push(eventInfo);
            }
          });
        }
      }
      return tableData;
    },
  },
  watch: {
    seasons: {
      immediate: true,
      deep: true,
      handler: function(newValue) {
        if (newValue && newValue.length) {
          this.fillRoundsAndGames();
          this.fillSeasonData();
        }
      },
    },
  },
  data() {
    return {
      gamesUrl: 'https://qyyibs1w0d.execute-api.us-west-2.amazonaws.com/prod/calendar/gamesBySeason?',
      // gamesUrl: 'https://kkd48smit2.execute-api.us-west-2.amazonaws.com/qa/calendar/gamesBySeason?',
      gamesByRound: [],
      categories: [],
      selectedGame: null,
      selectedCategory: null,
      selectedTime: null,
      gamesById: null,
    };
  },
  methods: {
    async fillEventActions() {
      try {
        const response = await axios.get('https://f2goh2kf1f.execute-api.us-east-2.amazonaws.com/prod/actions');
        this.categories = response.data.categories;
      } catch (e) {
        throw new Error(e);
      }
    },
    async fillSeasonData() {
      try {
        const season = this.seasons.filter(season => season.type === 0)[0];
        const response = await axios.get(
          `https://o7da51xd12.execute-api.us-east-2.amazonaws.com/prod/ta/events/${season.id}`,
        );
        if (response.data != '') {
          const games = response.data.games;
          games.forEach(game => {
            game.eventsById = game.events.reduce((accumulator, event) => {
              const storedEvents = accumulator.get(event.action_id);
              if (storedEvents) {
                storedEvents.push(event);
              } else {
                accumulator.set(event.action_id, [event]);
              }
              return accumulator;
            }, new Map());
          });
          this.gamesById = games.reduce((accumulator, game) => {
            accumulator.set(game.game_id, game);
            return accumulator;
          }, new Map());
        } else {
          this.gamesByRound = [];
          this.gamesById = null;
        }
      } catch (e) {
        throw new Error(e);
      }
    },
    async fillRoundsAndGames() {
      try {
        const gamesByRound = new Map();
        const { seasonsAndGames, seasonsAndTeams } = await this.fetchSeasonsGamesAndTeams(
          this.seasons.map(season => {
            return season.id;
          }),
          3,
        );
        const tournamentTeams = seasonsAndTeams.reduce((accumulator, seasonTeams) => {
          if (seasonTeams) {
            seasonTeams.teams.forEach(seasonTeam => {
              if (!accumulator.has(seasonTeam.team_id)) {
                accumulator.set(seasonTeam.team_id, seasonTeam);
              }
            });
          }
          return accumulator;
        }, new Map());
        seasonsAndGames.forEach(seasonGames => {
          seasonGames.games.forEach(game => {
            const gameDateTime = new Date(game.date_time_utc).valueOf();
            game.homeTeam = tournamentTeams.get(game.home_team);
            game.awayTeam = tournamentTeams.get(game.visiting_team);
            game.dateTime = gameDateTime;
            game.seasonId = seasonGames.season_id;
            const roundKey = `${seasonGames.season_type}-${game.matchday_abbreviation}`;
            const round = gamesByRound.get(roundKey);
            if (round) {
              round.games.push(game);
            } else {
              gamesByRound.set(roundKey, {
                key: roundKey,
                abbreviation: game.matchday_abbreviation,
                abbreviation2: this.getAbbreviation2(
                  game.matchday_abbreviation,
                  seasonGames.season_type,
                  game.seasonId,
                  game.isCup,
                ),
                games: [game],
              });
            }
          });
        });
        this.gamesByRound = Array.from(gamesByRound.values());
      } catch (e) {
        throw new Error(e);
      }
    },
    getAbbreviation2(abbreviation, seasonType, seasonId, isCup) {
      // eslint-disable-next-line no-param-reassign
      abbreviation = abbreviation.toUpperCase();
      let abbreviation2 = abbreviation.replace('M', 'J');
      if (isCup) {
        if (seasonId === 881) {
          const roundNumberString = abbreviation.replace('M', '');
          abbreviation2 = cupLegacyAbb[roundNumberString] || abbreviation2;
        } else {
          abbreviation2 = cup[abbreviation] ? abbreviation : abbreviation2;
        }
      } else {
        if (seasonType === 2) {
          if (['PI1', 'PI2'].includes(abbreviation)) {
            abbreviation2 = abbreviation
          } else {
            abbreviation2 = `${abbreviation.replace('PO', 'R')}`;
          }
        } else {
          abbreviation2 = regularLeague[abbreviation] || abbreviation2;
        }
      }
      return abbreviation2;
    },
    /**
     * @author Alejandro Martinez
     * @param {Array<number>} seasons - seasons ids
     * @param {number} retries - Times to retry the request
     * @param {String} createdUrl - Url to make the request
     * @return {Promise<{seasonsAndGames: *, seasonsAndTeams: *}|*|undefined>}
     */
    async fetchSeasonsGamesAndTeams(seasons, retries, createdUrl = '') {
      let gamesUrl = createdUrl;
      try {
        if (!gamesUrl) {
          gamesUrl = this.gamesUrl;
          seasons.forEach((seasonId, index) => {
            const prefix = index === 0 ? '' : '&';
            gamesUrl += `${prefix}seasons=${seasonId}`;
          });
        }
        const {
          data: { data, teams },
        } = await axios.get(gamesUrl);
        return { seasonsAndGames: data, seasonsAndTeams: teams };
      } catch (e) {
        if (retries > 0) {
          return this.fetchSeasonsGamesAndTeams(seasons, retries - 1, gamesUrl);
        }
        throw new Error(e);
      }
    },
    onChangeGameId(game) {
      this.selectedGame = game;
    },
    onChangeCategory(category) {
      this.selectedCategory = category;
    },
    onChangeTime(time) {
      this.selectedTime = time;
    },
  },
  created() {
    this.fillEventActions();
  },
};
</script>
<style lang="scss" scoped>
.events {
  width: 100%;
  height: 367px;
  background: rgb(255, 254, 254);
  &__cont-table {
    width: 100%;
    height: 227px;
    border-radius: 8px;
    border: solid 1px #132739;
    margin-top: 20px;
  }
}
</style>
